/**
 * Skeleton — content-shaped loading placeholders.
 *
 * The companion to {@link LoadingState}. A spinner says "we haven't started";
 * a content-shaped skeleton says "your answer is materializing" — the right
 * read for Avium's results-by-7am promise. Use a spinner for short, indefinite
 * waits (a save, a small panel); reach for these on the data-heavy answer
 * surfaces (The Brief, Capacity, Reports) where the layout is known ahead of
 * the data and a shape-matched placeholder removes the content "jump" on load.
 *
 * Tokens only — a slate-200 fill with a travelling shimmer band (the `shimmer`
 * keyframe in tailwind.config.js). More present than a bare opacity pulse, so
 * the surface clearly reads as "loading" rather than fading into the page.
 * Respects `prefers-reduced-motion` (the sweep is gated behind `motion-safe`);
 * the static slate-200 block remains as the reduced-motion placeholder.
 */

export interface SkeletonProps {
  /** Extra classes — drive size/shape with width/height + rounded utilities. */
  className?: string;
  /** Disable the shimmer (e.g. for a static placeholder). Default: animated. */
  animate?: boolean;
}

// The light sweep — an ::after band that slides across, clipped to the block's
// shape by the parent's `overflow-hidden`. Parked off the left edge when the
// animation isn't running (reduced motion), so it's invisible rather than a
// frozen white stripe.
const SHIMMER =
  "after:content-[''] after:absolute after:inset-0 after:-translate-x-full " +
  'after:bg-gradient-to-r after:from-transparent after:via-white/70 dark:after:via-white/10 after:to-transparent ' +
  'motion-safe:after:animate-shimmer';

/**
 * A single placeholder block. Shape it with `className` (w-/h-/rounded-).
 * Decorative by default — hidden from assistive tech; let the surrounding
 * region own the `role="status"`/`aria-busy` semantics.
 */
export default function Skeleton({ className = '', animate = true }: SkeletonProps) {
  return (
    <div
      className={`relative overflow-hidden bg-line dark:bg-card/10 ${animate ? SHIMMER : ''} ${className}`}
      aria-hidden="true"
    />
  );
}

/**
 * Wrap a group of skeletons so screen readers announce the load once and the
 * region is marked busy, instead of N silent decorative blocks.
 */
export function SkeletonGroup({
  label = 'Loading…',
  className = '',
  children,
}: {
  label?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className={className}>
      {children}
      <span className="sr-only">{label}</span>
    </div>
  );
}

/** One Risk Ledger card placeholder (mirrors BriefCard's rail + header + metric). */
export function BriefCardSkeleton() {
  return (
    <div className="bg-card rounded-2xl border border-[#E6ECEA] shadow-[0_2px_10px_rgba(15,23,42,0.04)] pl-6 pr-5 py-4 mb-3.5">
      <div className="flex items-center gap-2.5 mb-3">
        <Skeleton className="w-7 h-6 rounded-md" />
        <Skeleton className="w-24 h-5 rounded-full" />
        <Skeleton className="flex-1 max-w-[55%] h-5 rounded" />
      </div>
      <div className="flex items-end gap-4">
        <Skeleton className="w-16 h-9 rounded" />
        <Skeleton className="flex-1 h-4 rounded mb-1" />
      </div>
    </div>
  );
}

/** A column of Risk Ledger placeholders. */
export function BriefLedgerSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <SkeletonGroup label="Reading your signals…">
      {Array.from({ length: rows }).map((_, i) => (
        <BriefCardSkeleton key={i} />
      ))}
    </SkeletonGroup>
  );
}

/** One KPI gauge tile placeholder (label · value · bar · caption). */
export function KPIGaugeSkeleton() {
  return (
    <div className="rounded-lg border border-line bg-card p-4">
      <Skeleton className="h-3.5 w-28 rounded mb-3" />
      <Skeleton className="h-9 w-20 rounded mb-3" />
      <Skeleton className="h-2.5 w-full rounded-full mb-2" />
      <Skeleton className="h-3 w-32 rounded" />
    </div>
  );
}

/** A row of `count` KPI gauge placeholders. */
export function KPIGaugeRowSkeleton({ count = 3 }: { count?: number }) {
  return (
    <SkeletonGroup label="Loading capacity…" className="grid grid-cols-3 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <KPIGaugeSkeleton key={i} />
      ))}
    </SkeletonGroup>
  );
}
